import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowRight, MapPin, Home, Clock, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ddfAPI } from '../../services/api';

// City images for neighborhood cards - using actual active listing photos from each city
const CITY_IMAGES = {
  'Brantford': 'https://ddfcdn.realtor.ca/listing/TS639003557300930000/reb82/highres/0/x12596970_1.jpg',
  'Hamilton': 'https://ddfcdn.realtor.ca/listing/TS639071922696430000/reb82/highres/6/x12806346_1.jpg',
  'Burlington': 'https://ddfcdn.realtor.ca/listing/TS639071177889930000/reb82/highres/6/w12803646_1.jpg',
  'Oakville': 'https://ddfcdn.realtor.ca/listing/TS639071101454000000/reb15/highres/4/w12803054_1.jpg',
  'Mississauga': 'https://ddfcdn.realtor.ca/listing/TS639072706193770000/reb82/highres/8/w12805588_1.jpg',
  'Cambridge': 'https://ddfcdn.realtor.ca/listing/TS639057137827870000/reb14/highres/7/40802597_1.jpg',
  'Kitchener': 'https://ddfcdn.realtor.ca/listing/TS639070177062730000/reb14/highres/7/40804127_1.jpg',
  'Guelph': 'https://ddfcdn.realtor.ca/listing/TS639072678555070000/reb14/highres/8/40802658_1.jpg',
  'Waterloo': 'https://ddfcdn.realtor.ca/listing/TS639071723233770000/reb14/highres/7/40806117_1.jpg',
  'Milton': 'https://ddfcdn.realtor.ca/listing/TS639072670568070000/reb82/highres/0/w12799600_1.jpg',
};

// Default/fallback market stats for instant display
const DEFAULT_MARKET_STATS = [
  { city: 'Brantford', averagePrice: 625000, activeListings: 150, daysOnMarket: 28, dataType: 'active' },
  { city: 'Hamilton', averagePrice: 725000, activeListings: 320, daysOnMarket: 25, dataType: 'active' },
  { city: 'Burlington', averagePrice: 1150000, activeListings: 180, daysOnMarket: 22, dataType: 'active' },
  { city: 'Oakville', averagePrice: 1450000, activeListings: 210, daysOnMarket: 30, dataType: 'active' },
  { city: 'Mississauga', averagePrice: 1050000, activeListings: 450, daysOnMarket: 24, dataType: 'active' },
  { city: 'Cambridge', averagePrice: 750000, activeListings: 120, daysOnMarket: 26, dataType: 'active' },
];

// Cache key for localStorage
const MARKET_STATS_CACHE_KEY = 'conti_market_stats_cache';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

const NeighborhoodGuides = () => {
  // Initialize with cached data or defaults for instant display
  const [marketStats, setMarketStats] = useState(() => {
    try {
      const cached = localStorage.getItem(MARKET_STATS_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION && data?.length > 0) {
          return data;
        }
      }
    } catch (e) {}
    return DEFAULT_MARKET_STATS; // Fallback for instant display
  });
  const [loading, setLoading] = useState(false); // Start as not loading since we have data

  useEffect(() => {
    const fetchMarketStats = async () => {
      try {
        const data = await ddfAPI.getMarketStats();
        if (data.cities?.length > 0) {
          const newStats = data.cities.slice(0, 6);
          setMarketStats(newStats);
          // Cache the results
          localStorage.setItem(MARKET_STATS_CACHE_KEY, JSON.stringify({
            data: newStats,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.error('Error fetching market stats:', error);
        // Keep using cached/default data
      } finally {
        setLoading(false);
      }
    };
    fetchMarketStats();
  }, []);

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Market <span className="text-red-600">Insights</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Real market data based on recently sold properties across Ontario
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {marketStats.map((city) => {
            const cityImage = CITY_IMAGES[city.city] || 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800';
            const cityId = city.city.toLowerCase().replace(/\s+/g, '-');
            const isSoldData = city.dataType === 'sold';
            
            return (
              <Link key={city.city} to={`/search?location=${cityId}`}>
                <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 h-full border-0">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={cityImage} 
                      alt={city.city}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-2 text-white mb-1">
                        <MapPin size={16} />
                        <span className="font-semibold text-lg">{city.city}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-600">
                          Avg. ${(city.avgSoldPrice / 1000).toFixed(0)}K {isSoldData ? 'Sold' : ''}
                        </Badge>
                        <Badge variant="outline" className="text-white border-white/50">
                          <Home size={12} className="mr-1" />
                          {city.soldCount} {isSoldData ? 'Sold' : 'Active'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <p className="text-gray-600 text-sm mb-3">
                      {isSoldData 
                        ? `${city.soldCount} recently sold properties in ${city.city} with prices from $${(city.priceRange?.min / 1000).toFixed(0)}K to $${(city.priceRange?.max / 1000000).toFixed(1)}M`
                        : `Explore listings in ${city.city} starting from $${(city.priceRange?.min / 1000).toFixed(0)}K`
                      }
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-gray-100 rounded p-2">
                        <div className="font-bold text-gray-900">{city.soldCount}</div>
                        <div className="text-gray-500">{isSoldData ? 'Sold' : 'Listings'}</div>
                      </div>
                      <div className="bg-gray-100 rounded p-2">
                        <div className="font-bold text-gray-900">{city.avgDaysOnMarket}</div>
                        <div className="text-gray-500">Avg Days</div>
                      </div>
                      <div className="bg-gray-100 rounded p-2">
                        <div className="font-bold text-gray-900">${(city.medianPrice / 1000).toFixed(0)}K</div>
                        <div className="text-gray-500">Median</div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center text-red-600 font-medium text-sm group-hover:text-red-700">
                      View Properties in {city.city}
                      <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-8">
          <Link 
            to="/search" 
            className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors"
          >
            Search All Properties
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NeighborhoodGuides;
